import { bondOptions } from '../data/bondOptions';

// ─────────────────────────────────────────────────────────────────────────────
// Bond lookup
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Find a bond option object by its label (e.g. "81").
 * Returns the full bond object or null if not found.
 */
export const getBondByLabel = (label) => {
    if (!label) return null;
    return bondOptions.find((bond) => bond.label === label) || null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Stone count
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extracts the stone length from a size string like "3x3x110" → 110.
 * Returns 0 if the format is invalid.
 */
export const getStoneLengthFromSize = (sizeString) => {
    if (!sizeString) return 0;
    const dimensions = sizeString.split('x');
    if (dimensions.length < 3) return 0;
    return parseFloat(dimensions[dimensions.length - 1]) || 0;
};

/**
 * Calculates how many stones fit along the mould length,
 * and returns the total stone count across all moulds.
 */
export const calculateTotalStones = ({ mouldLength, stoneSize, numberOfMoulds, sticksPerMould, stonesPerStick }) => {
    const stoneLength     = getStoneLengthFromSize(stoneSize);
    const stonesPerLength = stoneLength && mouldLength
        ? Math.floor(mouldLength / stoneLength)
        : 1;
    const totalStones     = numberOfMoulds * sticksPerMould * stonesPerStick * stonesPerLength;
    return { totalStones, stonesPerLength };
};

// ─────────────────────────────────────────────────────────────────────────────
// Core sheet computation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Computes all derived values for a process sheet.
 *
 * @param {object} inputs  — raw form inputs (grit, bondType, concentration, dimensions, counts)
 * @param {object} bond    — resolved bond object from getBondByLabel()
 * @returns {object|null}  — all computed values, or null if inputs are incomplete
 */
export const computeSheet = (inputs, bond) => {
    if (!inputs || !bond) return null;

    const mouldWidth         = parseFloat(inputs.mouldWidth)        || 0;
    const mouldLength        = parseFloat(inputs.mouldLength)       || 0;
    const abrasiveLayerDepth = parseFloat(inputs.abrasiveLayer)      || 0;
    const slotWidth          = parseFloat(inputs.slotWidth)          || 0;
    const numberOfMoulds     = parseInt(inputs.numberOfMoulds)          || 0;
    const sticksPerMould     = parseInt(inputs.sticksPerMould)             || 0;
    const stonesPerStick     = parseInt(inputs.stonesPerStick)             || 0;
    const concentration      = parseFloat(inputs.concentration) || 0;

    // ── Volume ───────────────────────────────────────────────────────────────
    const effectiveWidth     = mouldWidth - slotWidth;
    const cupVolumeInCubicCm = (effectiveWidth * mouldLength * abrasiveLayerDepth) / 1000;

    // ── Per-cup weights ───────────────────────────────────────────────────────
    // Abrasive (grit) weight per cup: volume × abrasive density constant × concentration
    const abrasiveWeightPerCup = cupVolumeInCubicCm * 0.0088 * concentration;

    // Bond weight per cup: volume × bond-specific factor from bondOptions
    const bondWeightPerCup     = cupVolumeInCubicCm * bond.bstFactor;

    // ── Per-batch totals (all sticks across all moulds) ───────────────────────
    const totalAbrasiveWeight  = abrasiveWeightPerCup * sticksPerMould * numberOfMoulds;
    const totalBondWeight      = bondWeightPerCup     * sticksPerMould * numberOfMoulds;

    // ── Kryolite split ────────────────────────────────────────────────────────
    // Kryolite is a portion of total bond weight, defined as a % in bondOptions
    const kryoliteWeight       = bond.kryoPct
        ? totalBondWeight * (bond.kryoPct / 100)
        : 0;
    const pureBondWeight       = totalBondWeight - kryoliteWeight;

    // ── Pressures ─────────────────────────────────────────────────────────────
    const pressureBase         = ((effectiveWidth * mouldLength * sticksPerMould) / 100);
    const prePressure          = (pressureBase * bond.p1Factor) / 2;
    const mainPressure         = (pressureBase * bond.p2Factor) / 2;

    // ── Stone count ───────────────────────────────────────────────────────────
    const { totalStones, stonesPerLength } = calculateTotalStones({
        mouldLength,
        stoneSize:      inputs.stoneSize,
        numberOfMoulds,
        sticksPerMould,
        stonesPerStick,
    });

    return {
        effectiveWidth,
        cupVolumeInCubicCm,
        abrasiveWeightPerCup,
        bondWeightPerCup,
        totalAbrasiveWeight,
        totalBondWeight,
        kryoliteWeight,
        pureBondWeight,
        prePressure,
        mainPressure,
        sinteringTemperature: bond.temperature,
        totalStones,
        stonesPerLength,
    };
};

// ─────────────────────────────────────────────────────────────────────────────
// Default material plan rows
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Builds the three default inventory rows from computed values.
 * Each row can be linked to an inventory Item by the user.
 */
/**
 * @param {object} computed  — output of computeSheet()
 * @param {string} gritCode  — e.g. "B151-49", used as the abrasive row label
 * @param {string} bondType  — e.g. "81", used as the bond row label
 */
export const buildDefaultMaterialPlan = (computed, gritCode = '', bondType = '') => {
    if (!computed) return [];
    return [
        { itemId: null, itemName: gritCode || 'Abrasive (Grit)', quantityUsed: parseFloat(computed.totalAbrasiveWeight.toFixed(4)), unit: 'gm', _key: 'grit' },
        { itemId: null, itemName: bondType || 'Bond',            quantityUsed: parseFloat(computed.pureBondWeight.toFixed(4)),       unit: 'gm', _key: 'bond' },
        { itemId: null, itemName: 'Kryolite',                    quantityUsed: parseFloat(computed.kryoliteWeight.toFixed(4)),       unit: 'gm', _key: 'kryo' },
    ];
};

// ─────────────────────────────────────────────────────────────────────────────
// Grade string helper (for printing on sheet)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Assembles the display grade string from the three separate fields.
 * e.g. { grit: "B151-49", bondType: "81", concentration: 60 } → "B151-49-81-60"
 */
export const buildGradeString = ({ gritCode, bondType, concentration }) => {
    if (!gritCode || !bondType || concentration === '' || concentration === undefined) return '';
    return `${gritCode}-${bondType}-${concentration}`;
};